import React, { useState, useRef, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import csvService from '../services/csvService';
import manufacturerSalesService, { 
  ManufacturerSalesData, 
  BulkImportResult,
  ManufacturerSalesSummary 
} from '../services/manufacturerSalesService';
import { CSVFile } from '../types/data/csv';
import './ManufacturerSalesImportPage.css';

type ImportStep = 'upload' | 'preview' | 'importing' | 'complete';

interface DetectedColumn {
  name: string;
  type: 'required' | 'month' | 'other';
  index: number;
}

const ManufacturerSalesImportPage: React.FC = () => {
  const [currentStep, setCurrentStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedCSV, setParsedCSV] = useState<CSVFile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<BulkImportResult | null>(null);
  const [detectedColumns, setDetectedColumns] = useState<DetectedColumn[]>([]);
  const [summary, setSummary] = useState<ManufacturerSalesSummary | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load summary on mount
  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    try {
      const summaryData = await manufacturerSalesService.getSummary();
      setSummary(summaryData);
    } catch (err) {
      // Table might not exist yet, that's okay
      console.log('Summary not available yet');
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      setSelectedFile(file);
      setError(null);
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please select a CSV file');
      }

      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        throw new Error('File size must be less than 50MB');
      }
      
      // Parse CSV
      const csvFile = await csvService.parseCSV(file);
      setParsedCSV(csvFile);
      
      // Detect columns
      detectColumns(csvFile.columns);
      
      setCurrentStep('preview');
      
    } catch (err: any) {
      setError(err.message || 'Failed to parse CSV file');
    }
  };

  const detectColumns = (columns: string[]) => {
    const detected: DetectedColumn[] = [];
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                        'july', 'august', 'september', 'october', 'november', 'december'];

    columns.forEach((col, index) => {
      const colLower = col.toLowerCase().trim();
      
      // Check for required columns
      if (colLower.includes('brand') || colLower === 'manufacturer') {
        detected.push({ name: col, type: 'required', index });
      } else if (colLower.includes('pma') || colLower.includes('primary market')) {
        detected.push({ name: col, type: 'required', index });
      } else if (colLower.includes('segment')) {
        detected.push({ name: col, type: 'other', index });
      } else if (colLower.includes('model')) {
        detected.push({ name: col, type: 'other', index });
      } else if (colLower.includes('postal') || colLower.includes('district')) {
        detected.push({ name: col, type: 'other', index });
      } else if (colLower.includes('dealer') && colLower.includes('code')) {
        detected.push({ name: col, type: 'other', index });
      } else if (colLower.includes('sales') && colLower.includes('categor')) {
        detected.push({ name: col, type: 'other', index });
      } else {
        // Check for month columns
        const yearMonthMatch = colLower.match(/(\d{4})\s*-\s*(\w+)/);
        if (yearMonthMatch) {
          const monthName = yearMonthMatch[2].toLowerCase();
          if (monthNames.includes(monthName)) {
            detected.push({ name: col, type: 'month', index });
          } else {
            detected.push({ name: col, type: 'other', index });
          }
        } else if (monthNames.includes(colLower)) {
          detected.push({ name: col, type: 'month', index });
        } else if (col.toLowerCase() !== 'total') {
          detected.push({ name: col, type: 'other', index });
        }
      }
    });

    setDetectedColumns(detected);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleReset = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setParsedCSV(null);
    setError(null);
    setIsImporting(false);
    setImportProgress(0);
    setImportResult(null);
    setDetectedColumns([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!parsedCSV) return;

    setIsImporting(true);
    setCurrentStep('importing');
    setImportProgress(10);

    try {
      // Simulate progress
      setImportProgress(30);
      
      const result = await manufacturerSalesService.bulkImport(
        parsedCSV.columns,
        parsedCSV.rows
      );

      setImportProgress(100);
      setImportResult(result);
      setCurrentStep('complete');
      
      // Reload summary
      await loadSummary();

    } catch (err: any) {
      setError(err.message || 'Import failed');
      setCurrentStep('preview');
    } finally {
      setIsImporting(false);
    }
  };

  const monthColumns = detectedColumns.filter(c => c.type === 'month');
  const requiredColumns = detectedColumns.filter(c => c.type === 'required');

  return (
    <div className="manufacturer-sales-import-page">
      <Sidebar />
      <main className="manufacturer-sales-content">
        <div className="manufacturer-sales-header">
          <h1>📊 Market Sales Import</h1>
          <p>Import manufacturer sales data from CSV files with monthly columns</p>
        </div>

        {/* Summary Cards */}
        {summary && summary.totalRecords > 0 && (
          <div className="summary-cards">
            <div className="summary-card">
              <div className="summary-card-value">{summary.totalRecords.toLocaleString()}</div>
              <div className="summary-card-label">Records</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{summary.totalSalesItems.toLocaleString()}</div>
              <div className="summary-card-label">Sales Items</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{summary.totalAmountSold.toLocaleString()}</div>
              <div className="summary-card-label">Units Sold</div>
            </div>
            <div className="summary-card">
              <div className="summary-card-value">{summary.dateRange.earliest || 'N/A'}</div>
              <div className="summary-card-label">Earliest Period</div>
            </div>
          </div>
        )}

        {/* Step Indicator */}
        <div className="step-indicator">
          <div className={`step ${currentStep === 'upload' ? 'active' : 'completed'}`}>
            <span className="step-number">1</span>
            <span>Upload CSV</span>
          </div>
          <div className={`step-connector ${currentStep !== 'upload' ? 'completed' : ''}`} />
          <div className={`step ${currentStep === 'preview' ? 'active' : (currentStep === 'importing' || currentStep === 'complete' ? 'completed' : '')}`}>
            <span className="step-number">2</span>
            <span>Preview</span>
          </div>
          <div className={`step-connector ${currentStep === 'importing' || currentStep === 'complete' ? 'completed' : ''}`} />
          <div className={`step ${currentStep === 'importing' ? 'active' : (currentStep === 'complete' ? 'completed' : '')}`}>
            <span className="step-number">3</span>
            <span>Import</span>
          </div>
          <div className={`step-connector ${currentStep === 'complete' ? 'completed' : ''}`} />
          <div className={`step ${currentStep === 'complete' ? 'active' : ''}`}>
            <span className="step-number">4</span>
            <span>Complete</span>
          </div>
        </div>

        {/* Step Content */}
        {currentStep === 'upload' && (
          <div className="upload-section">
            <div 
              className={`upload-area ${isDragging ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="upload-icon">📁</div>
              <h3>Drag & drop your CSV file here</h3>
              <p>or click to browse</p>
              <span className="browse-button">Browse Files</span>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept=".csv"
                style={{ display: 'none' }}
              />
            </div>

            <div className="file-info" style={{ marginTop: '1.5rem' }}>
              <h4 style={{ margin: '0 0 0.5rem 0' }}>Expected CSV Format:</h4>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 0.5rem 0' }}>
                Your CSV should have columns for: Brands, Segmentations, Models, PMA, Postal District, Dealer Code, Sales Categories
              </p>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                Month columns should be named like "2025 - January", "2025 - February", etc. with sales values.
              </p>
            </div>

            {error && <div className="error-message">{error}</div>}
          </div>
        )}

        {currentStep === 'preview' && parsedCSV && (
          <div className="preview-section">
            <div className="preview-header">
              <h2>Data Preview</h2>
              <div className="preview-stats">
                <div className="preview-stat">
                  <div className="preview-stat-value">{parsedCSV.rows.length.toLocaleString()}</div>
                  <div className="preview-stat-label">Rows</div>
                </div>
                <div className="preview-stat">
                  <div className="preview-stat-value">{parsedCSV.columns.length}</div>
                  <div className="preview-stat-label">Columns</div>
                </div>
                <div className="preview-stat">
                  <div className="preview-stat-value">{monthColumns.length}</div>
                  <div className="preview-stat-label">Month Columns</div>
                </div>
              </div>
            </div>

            {/* Column Detection */}
            <div className="column-detection">
              <h3>Detected Columns</h3>
              <div className="detected-columns">
                {detectedColumns.map((col, idx) => (
                  <span 
                    key={idx} 
                    className={`column-tag ${col.type === 'required' ? 'required' : col.type === 'month' ? 'month' : ''}`}
                  >
                    {col.type === 'required' && '✓ '}
                    {col.type === 'month' && '📅 '}
                    {col.name}
                  </span>
                ))}
              </div>
              {requiredColumns.length < 2 && (
                <p style={{ color: 'var(--warning)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  ⚠️ Warning: Some required columns (Brands, PMA) may not be detected. Import may fail.
                </p>
              )}
              {monthColumns.length === 0 && (
                <p style={{ color: 'var(--error)', marginTop: '0.5rem', fontSize: '0.875rem' }}>
                  ❌ Error: No month columns detected. Please ensure your CSV has month columns like "2025 - January".
                </p>
              )}
            </div>

            {/* Data Preview Table */}
            <div className="data-preview-table">
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    {parsedCSV.columns.slice(0, 12).map((col, idx) => (
                      <th key={idx}>{col}</th>
                    ))}
                    {parsedCSV.columns.length > 12 && <th>...</th>}
                  </tr>
                </thead>
                <tbody>
                  {parsedCSV.rows.slice(0, 10).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td>{rowIdx + 1}</td>
                      {row.slice(0, 12).map((cell, cellIdx) => (
                        <td key={cellIdx}>{cell || '-'}</td>
                      ))}
                      {row.length > 12 && <td>...</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
              {parsedCSV.rows.length > 10 && (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: '0.5rem' }}>
                  Showing 10 of {parsedCSV.rows.length.toLocaleString()} rows
                </p>
              )}
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="action-buttons">
              <button className="btn btn-secondary" onClick={handleReset}>
                ← Back
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleImport}
                disabled={monthColumns.length === 0}
              >
                Import Data →
              </button>
            </div>
          </div>
        )}

        {currentStep === 'importing' && (
          <div className="import-progress">
            <div className="progress-spinner" />
            <h2>Importing Data...</h2>
            <div className="progress-bar-container">
              <div 
                className="progress-bar" 
                style={{ width: `${importProgress}%` }}
              />
            </div>
            <p className="progress-text">
              Processing {parsedCSV?.rows.length.toLocaleString()} rows...
            </p>
          </div>
        )}

        {currentStep === 'complete' && importResult && (
          <div className="import-results">
            <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              ✅ Import Complete
            </h2>

            <div className="results-summary">
              <div className="result-card success">
                <div className="result-value">{importResult.created}</div>
                <div className="result-label">Created</div>
              </div>
              <div className="result-card warning">
                <div className="result-value">{importResult.updated}</div>
                <div className="result-label">Updated</div>
              </div>
              <div className="result-card error">
                <div className="result-value">{importResult.failed}</div>
                <div className="result-label">Failed</div>
              </div>
            </div>

            {importResult.errors.length > 0 && (
              <div className="error-list">
                <h4>Errors ({importResult.errors.length})</h4>
                {importResult.errors.map((err, idx) => (
                  <div key={idx} className="error-item">
                    Row {err.row}: {err.error}
                  </div>
                ))}
              </div>
            )}

            <div className="action-buttons">
              <button className="btn btn-secondary" onClick={handleReset}>
                Import Another File
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManufacturerSalesImportPage;

